// Each country's own currency (ISO code), for the Admin Portal's billing:
// a company is billed in the currency of its Country. Keyed by the names in
// utils/countries.ts. A country not listed here (or a name typed by hand)
// falls back to EUR.

const BY_CURRENCY: Record<string, string[]> = {
  EUR: ['Andorra', 'Austria', 'Belgium', 'Bulgaria', 'Croatia', 'Cyprus', 'Estonia', 'Finland',
        'France', 'Germany', 'Greece', 'Ireland', 'Italy', 'Kosovo', 'Latvia', 'Lithuania',
        'Luxembourg', 'Malta', 'Monaco', 'Montenegro', 'Netherlands', 'Portugal', 'San Marino',
        'Slovakia', 'Slovenia', 'Spain', 'Vatican City'],
  USD: ['United States', 'Ecuador', 'El Salvador', 'Marshall Islands', 'Micronesia', 'Palau',
        'Panama', 'Timor-Leste', 'Zimbabwe'],
  AUD: ['Australia', 'Kiribati', 'Nauru', 'Tuvalu'],
  CHF: ['Switzerland', 'Liechtenstein'],
  XOF: ['Benin', 'Burkina Faso', 'Côte d’Ivoire', 'Guinea-Bissau', 'Mali', 'Niger', 'Senegal', 'Togo'],
  XAF: ['Cameroon', 'Central African Republic', 'Chad', 'Congo (Brazzaville)', 'Equatorial Guinea', 'Gabon'],
  XCD: ['Antigua and Barbuda', 'Dominica', 'Grenada', 'Saint Kitts and Nevis', 'Saint Lucia',
        'Saint Vincent and the Grenadines'],
  ILS: ['Israel', 'Palestine'],
};

const ONE: Record<string, string> = {
  Afghanistan: 'AFN', Albania: 'ALL', Algeria: 'DZD', Angola: 'AOA', Argentina: 'ARS',
  Armenia: 'AMD', Azerbaijan: 'AZN', Bahamas: 'BSD', Bahrain: 'BHD', Bangladesh: 'BDT',
  Barbados: 'BBD', Belarus: 'BYN', Belize: 'BZD', Bhutan: 'BTN', Bolivia: 'BOB',
  'Bosnia and Herzegovina': 'BAM', Botswana: 'BWP', Brazil: 'BRL', Brunei: 'BND',
  Burundi: 'BIF', 'Cabo Verde': 'CVE', Cambodia: 'KHR', Canada: 'CAD', Chile: 'CLP',
  China: 'CNY', Colombia: 'COP', Comoros: 'KMF', 'Congo (Kinshasa)': 'CDF', 'Costa Rica': 'CRC',
  Cuba: 'CUP', Czechia: 'CZK', Denmark: 'DKK', Djibouti: 'DJF', 'Dominican Republic': 'DOP',
  Egypt: 'EGP', Eritrea: 'ERN', Eswatini: 'SZL', Ethiopia: 'ETB', Fiji: 'FJD', Gambia: 'GMD',
  Georgia: 'GEL', Ghana: 'GHS', Guatemala: 'GTQ', Guinea: 'GNF', Guyana: 'GYD', Haiti: 'HTG',
  Honduras: 'HNL', 'Hong Kong': 'HKD', Hungary: 'HUF', Iceland: 'ISK', India: 'INR',
  Indonesia: 'IDR', Iran: 'IRR', Iraq: 'IQD', Jamaica: 'JMD', Japan: 'JPY', Jordan: 'JOD',
  Kazakhstan: 'KZT', Kenya: 'KES', Kuwait: 'KWD', Kyrgyzstan: 'KGS', Laos: 'LAK',
  Lebanon: 'LBP', Lesotho: 'LSL', Liberia: 'LRD', Libya: 'LYD', Macau: 'MOP',
  Madagascar: 'MGA', Malawi: 'MWK', Malaysia: 'MYR', Maldives: 'MVR', Mauritania: 'MRU',
  Mauritius: 'MUR', Mexico: 'MXN', Moldova: 'MDL', Mongolia: 'MNT', Morocco: 'MAD',
  Mozambique: 'MZN', Myanmar: 'MMK', Namibia: 'NAD', Nepal: 'NPR', 'New Zealand': 'NZD',
  Nicaragua: 'NIO', Nigeria: 'NGN', 'North Korea': 'KPW', 'North Macedonia': 'MKD',
  Norway: 'NOK', Oman: 'OMR', Pakistan: 'PKR', 'Papua New Guinea': 'PGK', Paraguay: 'PYG',
  Peru: 'PEN', Philippines: 'PHP', Poland: 'PLN', Qatar: 'QAR', Romania: 'RON', Russia: 'RUB',
  Rwanda: 'RWF', Samoa: 'WST', 'São Tomé and Príncipe': 'STN', 'Saudi Arabia': 'SAR',
  Serbia: 'RSD', Seychelles: 'SCR', 'Sierra Leone': 'SLE', Singapore: 'SGD',
  'Solomon Islands': 'SBD', Somalia: 'SOS', 'South Africa': 'ZAR', 'South Korea': 'KRW',
  'South Sudan': 'SSP', 'Sri Lanka': 'LKR', Sudan: 'SDG', Suriname: 'SRD', Sweden: 'SEK',
  Syria: 'SYP', Taiwan: 'TWD', Tajikistan: 'TJS', Tanzania: 'TZS', Thailand: 'THB',
  Tonga: 'TOP', 'Trinidad and Tobago': 'TTD', Tunisia: 'TND', Turkey: 'TRY',
  Turkmenistan: 'TMT', Uganda: 'UGX', Ukraine: 'UAH', 'United Arab Emirates': 'AED',
  'United Kingdom': 'GBP', Uruguay: 'UYU', Uzbekistan: 'UZS', Vanuatu: 'VUV',
  Venezuela: 'VES', Vietnam: 'VND', Yemen: 'YER', Zambia: 'ZMW',
};

const MAP = new Map<string, string>(Object.entries(ONE).map(([c, cur]) => [c.toLowerCase(), cur]));
for (const [cur, list] of Object.entries(BY_CURRENCY)) for (const c of list) MAP.set(c.toLowerCase(), cur);

/** "India" → "INR", "belgium" → "EUR"; unknown → "EUR". */
export function currencyOfCountry(country: string | null | undefined): string {
  return MAP.get((country ?? '').trim().toLowerCase()) ?? 'EUR';
}

/** Every currency some country here uses, for the Billing Currency list. */
export const COUNTRY_CURRENCIES: string[] = [...new Set(MAP.values())].sort();
